import React from 'react';
import {
  Area,
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  Legend,
  Line,
  ReferenceLine,
  Pie,
  PieChart,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {humanise, monthYear, monthYearShort} from './labels';

/**
 * Chart shell shared by every report.
 *
 * Owns the palette, the responsive container, and — importantly — the empty
 * state. Several series in this system are legitimately all-zero (collections on
 * stage, prints between December and March), so "no data for these filters" has
 * to be a real state rather than an axis with nothing on it.
 *
 * Individual reports pass data and a series config; they never import recharts
 * directly, so swapping the charting library later touches only this file.
 */

export const CHART_COLOURS = [
  '#0F8F82', '#3D6DB5', '#C9873A', '#8A5FA8',
  '#B04A5A', '#4E8C4A', '#7A8590'
];

const money = (v: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(v || 0);

const compact = (v: number) => {
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(v);
};

interface Shell {
  title: string;
  subtitle?: string;
  loading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  height?: number;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}

export const ChartCard = ({
  title,
  subtitle,
  loading,
  isEmpty,
  emptyMessage,
  height = 300,
  children,
  actions
}: Shell) => (
  <div className="card">
    <div className="card-header d-flex justify-content-between align-items-center">
      <div>
        <h3 className="card-title float-none d-block text-capitalize">{title}</h3>
        {subtitle && (
          <div>
            <small className="text-muted">{subtitle}</small>
          </div>
        )}
      </div>
      {actions}
    </div>
    <div className="card-body" style={{minHeight: height}}>
      {loading && (
        <div className="text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm mr-2" role="status" />
          Loading
        </div>
      )}
      {!loading && isEmpty && (
        <div className="text-center text-muted py-5">
          <i className="fas fa-chart-bar fa-2x mb-3 d-block" />
          {emptyMessage || 'No data for these filters.'}
        </div>
      )}
      {!loading && !isEmpty && children}
    </div>
  </div>
);

interface BarProps {
  data: any[];
  xKey: string;
  yKey: string;
  height?: number;
  currency?: boolean;
  colour?: string;
  /** Tooltip name for the bars. Defaults to the readable form of `yKey`. */
  valueName?: string;
}

/** Vertical bars over a time axis. */
export const ReportBarChart = ({
  data,
  xKey,
  yKey,
  height = 280,
  currency = true,
  colour = CHART_COLOURS[0],
  valueName
}: BarProps) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{top: 8, right: 12, left: 4, bottom: 4}}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E9ECEF" />
      <XAxis
        dataKey={xKey}
        tick={{fontSize: 11}}
        tickMargin={6}
        tickFormatter={(v: any) => monthYearShort(v)}
      />
      <YAxis tick={{fontSize: 11}} tickFormatter={compact} width={52} />
      <Tooltip
        formatter={(v: any) => (currency ? money(Number(v)) : v)}
        labelFormatter={(label: any) => monthYear(label)}
        labelStyle={{fontWeight: 700}}
      />
      <Bar
        dataKey={yKey}
        name={valueName || humanise(yKey)}
        fill={colour}
        radius={[3, 3, 0, 0]}
      />
    </BarChart>
  </ResponsiveContainer>
);

interface DonutProps {
  data: any[];
  nameKey: string;
  valueKey: string;
  height?: number;
}

/** Composition, as a donut with a legend. */
export const ReportDonutChart = ({
  data,
  nameKey,
  valueKey,
  height = 280
}: DonutProps) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Pie
        data={data}
        dataKey={valueKey}
        nameKey={nameKey}
        innerRadius="52%"
        outerRadius="78%"
        paddingAngle={1}
        minAngle={3}>
        {data.map((entry, i) => (
          <Cell
            key={`${entry[nameKey]}-${i}`}
            fill={CHART_COLOURS[i % CHART_COLOURS.length]}
          />
        ))}
      </Pie>
      <Tooltip formatter={(v: any, name: any) => [money(Number(v)), humanise(name)]} />
      <Legend
        layout="vertical"
        align="right"
        verticalAlign="middle"
        wrapperStyle={{fontSize: 11}}
        formatter={(value: any) => humanise(value)}
      />
    </PieChart>
  </ResponsiveContainer>
);

interface SeriesDef {
  key: string;
  name: string;
  colour?: string;
  /** 'bar' (default) or 'line' — a line can sit on the same or a second axis. */
  type?: 'bar' | 'line';
  /** 'right' puts the series on a secondary axis (e.g. a count beside money). */
  axis?: 'left' | 'right';
  /** Bars sharing a stackId are stacked rather than grouped. */
  stackId?: string;
  /**
   * How the tooltip shows this series. Defaults to the chart's `currency`
   * setting, except that a series on the right axis is a count and is never
   * shown as money.
   */
  format?: 'currency' | 'number' | 'percent';
}

interface MultiProps {
  data: any[];
  xKey: string;
  series: SeriesDef[];
  height?: number;
  currency?: boolean;
}

/**
 * Mixed bars and lines over one axis, with optional second axis.
 *
 * Reused for anything combining measures — invoiced vs collected, or value
 * (bars) against a participating-franchise count (line on the right axis).
 * Reports never touch recharts directly.
 */
export const ReportMultiSeriesChart = ({
  data,
  xKey,
  series,
  height = 280,
  currency = true
}: MultiProps) => {
  const hasRight = series.some((s) => s.axis === 'right');
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{top: 8, right: 12, left: 4, bottom: 4}}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E9ECEF" />
        <XAxis
        dataKey={xKey}
        tick={{fontSize: 11}}
        tickMargin={6}
        tickFormatter={(v: any) => monthYearShort(v)}
      />
        <YAxis yAxisId="left" tick={{fontSize: 11}} tickFormatter={compact} width={52} />
        {hasRight && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{fontSize: 11}}
            width={40}
            allowDecimals={false}
          />
        )}
        <Tooltip
          labelFormatter={(label: any) => monthYear(label)}
          formatter={(v: any, _name: any, item: any) => {
            const s = series.find((x) => x.key === item?.dataKey);
            if (s?.format === 'percent') return `${v}%`;
            const asMoney = s?.format
              ? s.format === 'currency'
              : currency && s?.axis !== 'right';
            return asMoney ? money(Number(v)) : v;
          }}
        />
        <Legend wrapperStyle={{fontSize: 11}} />
        {series.map((s, i) => {
          const colour = s.colour || CHART_COLOURS[i % CHART_COLOURS.length];
          const yAxisId = s.axis === 'right' ? 'right' : 'left';
          if (s.type === 'line') {
            return (
              <Line
                key={s.key}
                yAxisId={yAxisId}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={colour}
                strokeWidth={2}
                dot={{r: 2}}
              />
            );
          }
          return (
            <Bar
              key={s.key}
              yAxisId={yAxisId}
              dataKey={s.key}
              name={s.name}
              fill={colour}
              stackId={s.stackId}
              radius={s.stackId ? undefined : [3, 3, 0, 0]}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

interface AreaProps {
  data: any[];
  xKey: string;
  topKey: string;
  fillKey: string;
  topName: string;
  fillName: string;
  height?: number;
}

/**
 * A filled series beneath a line — used for invoiced (line) over collected
 * (fill), so the uncollected gap between them is the visible story.
 */
export const ReportGapAreaChart = ({
  data,
  xKey,
  topKey,
  fillKey,
  topName,
  fillName,
  height = 280
}: AreaProps) => (
  <ResponsiveContainer width="100%" height={height}>
    <ComposedChart data={data} margin={{top: 8, right: 12, left: 4, bottom: 4}}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E9ECEF" />
      <XAxis
        dataKey={xKey}
        tick={{fontSize: 11}}
        tickMargin={6}
        tickFormatter={(v: any) => monthYearShort(v)}
      />
      <YAxis tick={{fontSize: 11}} tickFormatter={compact} width={52} />
      <Tooltip
        formatter={(v: any) => money(Number(v))}
        labelFormatter={(label: any) => monthYear(label)}
      />
      <Legend wrapperStyle={{fontSize: 11}} />
      <Area
        type="monotone"
        dataKey={fillKey}
        name={fillName}
        stroke={CHART_COLOURS[5]}
        fill={CHART_COLOURS[5]}
        fillOpacity={0.25}
      />
      <Line
        type="monotone"
        dataKey={topKey}
        name={topName}
        stroke={CHART_COLOURS[1]}
        strokeWidth={2}
        dot={{r: 2}}
      />
    </ComposedChart>
  </ResponsiveContainer>
);

/** Semantic colours for outcome-style categories, separate from the palette. */
export const OUTCOME_COLOURS: Record<string, string> = {
  in_progress: '#3D6DB5',
  at_risk: '#C9873A',
  won: '#4E8C4A',
  lost: '#B04A5A'
};

/** Fulfilment speed / data-quality tone. */
export const TONE_COLOURS: Record<string, string> = {
  good: '#4E8C4A',
  ok: '#3D6DB5',
  slow: '#C9873A',
  invalid: '#B04A5A'
};

/** Direction of a pipeline move — forward is neutral, backward is a warning. */
export const DIRECTION_COLOURS: Record<string, string> = {
  forward: '#3D6DB5',
  won: '#4E8C4A',
  backward: '#C9873A',
  exit: '#B04A5A'
};

interface HBarProps {
  data: any[];
  labelKey: string;
  valueKey: string;
  height?: number;
  currency?: boolean;
  colour?: string;
  /** Field whose value selects a colour from `colourMap` (e.g. outcome). */
  colourField?: string;
  colourMap?: Record<string, string>;
  /** Category axis width — widen for long labels like status transitions. */
  labelWidth?: number;
  /** Draws a vertical benchmark line, e.g. a network average. */
  referenceValue?: number;
  referenceLabel?: string;
  /** Tooltip name for the bars. Defaults to the readable form of `valueKey`. */
  valueName?: string;
}

/**
 * Horizontal bars, best for ranked categories — a league table of top spenders,
 * or a short set of subjects where a donut would waste the labels.
 */
export const ReportHorizontalBarChart = ({
  data,
  labelKey,
  valueKey,
  height = 280,
  currency = true,
  colour = CHART_COLOURS[0],
  colourField,
  colourMap,
  labelWidth = 150,
  referenceValue,
  referenceLabel,
  valueName
}: HBarProps) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart
      data={data}
      layout="vertical"
      margin={{top: referenceValue !== undefined ? 28 : 8, right: 16, left: 8, bottom: 4}}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E9ECEF" />
      <XAxis type="number" tick={{fontSize: 11}} tickFormatter={compact} />
      <YAxis
        type="category"
        dataKey={labelKey}
        tick={{fontSize: 11}}
        tickFormatter={(v: any) => humanise(v)}
        width={labelWidth}
      />
      <Tooltip
        formatter={(v: any) => (currency ? money(Number(v)) : v)}
        labelFormatter={(label: any) => humanise(label)}
      />
      {referenceValue !== undefined && (
        <ReferenceLine
          x={referenceValue}
          stroke="#B04A5A"
          strokeDasharray="4 3"
          label={{
            value: referenceLabel || String(referenceValue),
            position: 'top',
            fontSize: 12,
            fontWeight: 600,
            fill: '#B04A5A'
          }}
        />
      )}
      <Bar
        dataKey={valueKey}
        name={valueName || humanise(valueKey)}
        radius={[0, 3, 3, 0]}
        fill={colour}>
        {colourField && colourMap
          ? data.map((row, i) => (
              <Cell
                key={`${row[labelKey]}-${i}`}
                fill={colourMap[row[colourField]] || colour}
              />
            ))
          : null}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);
